import React, { useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { FiAlertCircle, FiCheck } from 'react-icons/fi';
import { HiOutlineOfficeBuilding } from 'react-icons/hi';
import { useMutation, useQueryClient } from 'react-query';
import { createOrg } from '../../api/org';

type Props = {
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
}

function CreateOrg({ show, setShow }: Props) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [error, setError] = useState<string | null>(null);

    const handleClose = () => {
        setShow(false);
        setName('');
        setDescription('');
        setError(null);
    };

    const queryClient = useQueryClient();
    const { mutateAsync: createOrgFn, isLoading } = useMutation({
        mutationFn: createOrg,
        onSuccess: () => {
            queryClient.invalidateQueries(['Organizations']);
            handleClose();
        },
        onError: (error: any) => {
            setError(error?.response?.data?.message || "Operation failed. Please try again or contact admin.");
        },
    });

    const handleCreate = async () => {
        if (!name.trim()) {
            setError('Organization name is required');
            return;
        }
        setError(null);
        await createOrgFn({ name: name.trim(), description: description.trim() });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        handleCreate();
    };

    return (
        <Modal 
            show={show} 
            onHide={handleClose}
            centered
            size="lg"
            contentClassName="bg-dark text-light"
        >
            <Modal.Header 
                closeButton 
                className="border-0 pb-0"
                style={{ 
                    backgroundColor: '#2d2d2d', 
                    borderBottom: '1px solid #444'
                }}
            >
                <Modal.Title className="d-flex align-items-center">
                    <HiOutlineOfficeBuilding className="me-2 text-primary" size={24} />
                    Create New Organization
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ backgroundColor: '#2d2d2d' }}>
                <div className="mb-4">
                    <p className="mb-0" style={{color: '#ccc'}}>
                        Organizations help you organize your projects and teams. Each organization 
                        has its own agents, data sources, and member management.
                    </p>
                </div>

                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-4">
                        <Form.Label className="fw-semibold mb-2">
                            Organization Name *
                        </Form.Label>
                        <Form.Control
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Enter organization name (e.g., Acme Inc.)"
                            disabled={isLoading}
                            className="bg-dark text-light border-secondary"
                            style={{
                                borderColor: error && !name.trim() ? '#dc3545' : '#444',
                                '--bs-border-color': '#444',
                                '--bs-focus-border-color': '#0d6efd'
                            } as any}
                        />
                        <Form.Text style={{color: '#ccc'}}>
                            Choose a descriptive name for your organization
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-4">
                        <Form.Label className="fw-semibold mb-2">
                            Description (Optional)
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Describe your organization's purpose or focus areas..."
                            disabled={isLoading}
                            className="bg-dark text-light border-secondary"
                            style={{
                                borderColor: '#444',
                                '--bs-border-color': '#444',
                                '--bs-focus-border-color': '#0d6efd',
                                resize: 'none'
                            } as any}
                        />
                        <Form.Text style={{color: '#ccc'}}>
                            Help team members understand what this organization is for
                        </Form.Text>
                    </Form.Group>

                    {error && (
                        <Alert 
                            variant="danger" 
                            className="d-flex align-items-center mb-4"
                            style={{ 
                                backgroundColor: 'rgba(220, 53, 69, 0.1)', 
                                borderColor: '#dc3545',
                                color: '#f5c6cb'
                            }}
                        >
                            <FiAlertCircle className="me-2" size={16} />
                            {error}
                        </Alert>
                    )}
                </Form>
            </Modal.Body>

            <Modal.Footer 
                className="border-0 pt-0"
                style={{ backgroundColor: '#2d2d2d' }}
            >
                <div className="d-flex gap-2 w-100">
                    <Button 
                        variant="outline-secondary" 
                        onClick={handleClose}
                        disabled={isLoading}
                        className="flex-grow-1"
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleCreate}
                        disabled={isLoading || !name.trim()}
                        className="flex-grow-1 d-flex align-items-center justify-content-center gap-2"
                    >
                        {isLoading ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                />
                                Creating...
                            </>
                        ) : (
                            <>
                                <FiCheck size={16} />
                                Create Organization
                            </>
                        )}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}

export default CreateOrg;